import React, {Component} from 'react';
import {
  Row,
  Card,
  CardBody,
  CardTitle, Button,
} from 'reactstrap';
import ReactTable from 'react-table';
import {withTranslation} from 'react-i18next';
import {Colxx} from '../components/common/CustomBootstrap';
import {application, reactTableText} from '../constants/configs';
import {keyBy, mapValues, omit, capitalize, values, find} from 'lodash';
import moment from 'moment';
import Api from '../services/Api';
import {
  i18nizeDate,
  getDatePickerProps,
  numberWithCommas,
  mapStatus,
  getStatusColor,
  getSideColor,
  fixedToWithoutRound,
  stringToNumber,
  selectStyles,
} from '../helpers/Utils';
import {store} from '../AppRenderer';
import {createMuiTheme, ThemeProvider} from '@material-ui/core/styles';
import {DateTimePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {NotificationManager} from '../components/common/react-notifications';
import ModalManager from '../components/modal/ModalManager';
import PlaceOrderForm from "./PlaceOrderForm";
import JalaliUtils from '@date-io/jalaali';
import MomentUtils from '@date-io/moment';
import i18n from '../services/i18n';
import Rbac from '../components/Rbac';
import Select from 'react-select';
import fa_IR from "../constants/locales";

class Orders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      orders: [],
      pages: -1,
      loading: false,
      selectedStartDate: null,
      selectedEndDate: null,
      selectedType: '',
    };
    this.pairs = keyBy(store.getState().general.pairs, 'name');
    this.typeOptions = [
      {label: props.t('Normal Order'), value: ''},
      {label: props.t('Stop Order'), value: 'STOP_LIMIT'},
    ];
  }

  fetchData = async state => {
    try {
      this.setState({loading: true});
      const offset = state.page * state.pageSize;
      const {data} = await Api.invoke(
        'GET',
        '/admin-ui/user-orders',
        null,
        {
          params: {
            userId: this.props.userId,
            offset,
            limit: state.pageSize,
            ...(state.filtered.length ? omit(mapValues(keyBy(state.filtered, 'id'), 'value'), 'createdAt') : null),
            ...(this.state.selectedStartDate ? {createdFrom: moment(this.state.selectedStartDate).toISOString()} : null),
            ...(this.state.selectedEndDate ? {createdTo: moment(this.state.selectedEndDate).toISOString()} : null),
            ...(this.state.selectedType ? {condition: this.state.selectedType} : null)
          }
        },
      );
      this.setState({
        orders: data.orders,
        pages: data.totalPage,
      });
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  handleDateChange = (type, date) => {
    this.setState({
      [type]: date,
    });
  };

  onChangeType = selected => {
    this.setState({
      selectedType: selected.value,
    }, async () => {
      await this.fetchData({
        ...this.table.state,
        filtered: [],
        page: 0,
      });
    });
  };

  cancelOrder = async orderId => {
    const {t, userId} = this.props;
    try {
      await Api.invoke(
        'DELETE',
        '/admin-ui/user-order',
        {
          orderId,
          userId,
        },
      );
      NotificationManager.success(t('Order canceled successfully'), t('Message'));
      this.table.fireFetchData();
    } catch (e) {
      console.log('error: ', e);
    }
  };

  onPressCancel = orderId => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Confirm Cancel'),
      body: t('Are you sure you want to cancel this order ?'),
      buttons: [
        {
          color: 'primary',
          onClick: async () => {
            await this.cancelOrder(orderId);
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Accept'),
        },
        {
          color: 'grey',
          onClick: () => {
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Close'),
        },
      ],
    });
  };

  onPressCreateOrder = () => {
    const {t, userId, wallets, profile, fetchWallets} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Place Order'),
      body: <PlaceOrderForm
        userId={userId}
        wallets={wallets}
        tradeFee={profile.tradeFee}
        fetchWallets={fetchWallets}
        afterSubmit={() => {
          ModalManager.close({isOpen: false});
          this.table.fireFetchData();
        }}
      />,
      buttons: [
        {
          color: 'light',
          onClick: () => {
            ModalManager.close({
              isOpen: false
            });
          },
          text: i18n.t('Close'),
        },
      ],
    });
  };

  mapStopOrderStatus = status => {
    const {t} = this.props;
    switch (status) {
      case 'PENDING':
        return t('Waiting');
      case 'CONFIRMED':
        return t('Activated');
      default:
        return mapStatus(status);
    }
  };

  getNormalColumns = t => [
    {
      Header: t('Pair'),
      accessor: 'pair',
      filterable: true,
      minWidth: 120,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          {values(this.pairs).map(pair => (
            <option key={pair.name} value={pair.name}>{pair.localeName}</option>
          ))}
        </select>
      ),
      Cell: props => <span>{this.pairs[props.value] ? this.pairs[props.value].localeName : props.value}</span>,
    },
    {
      Header: t('Type'),
      accessor: 'type',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="LIMIT">{t('Limit')}</option>
          <option value="MARKET">{t('Market')}</option>
        </select>
      ),
      Cell: props => <span>{t(capitalize(props.value))}</span>
    },
    {
      Header: t('Side'),
      accessor: 'side',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="BUY">{t('Buy')}</option>
          <option value="SELL">{t('Sell')}</option>
        </select>
      ),
      Cell: props => <span className={getSideColor(props.value)}>{t(capitalize(props.value))}</span>,
    },
    {
      Header: t('Price'),
      accessor: 'price',
      Cell: props => {
        const {side, filledPrice, type, pair} = props.original;
        return (
          <span className={getSideColor(side)}>
            {type === 'MARKET' ?
              fixedToWithoutRound(
                Number(filledPrice),
                this.pairs[pair] ? this.pairs[pair].priceDecimals : undefined,
              )
              :
              numberWithCommas(Number(props.value))
            }
          </span>
        );
      },
    },
    {
      Header: t('Amount'),
      accessor: 'amount',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>,
    },
    {
      Header: t('Filled Amount'),
      accessor: 'filledAmount',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>,
    },
    {
      Header: t('Total'),
      Cell: props => {
        const {type, trades, price, amount, pair} = props.original;
        return (
          <span>
            {type === 'MARKET' ?
              trades.length === 0 ?
              0
              :
              fixedToWithoutRound(
                trades.map(item =>
                  Number(stringToNumber(fixedToWithoutRound(
                    Number(item.price) * Number(item.amount),
                      this.pairs[item.pair] ? this.pairs[item.pair].priceDecimals : undefined,
                    ),
                  )),
                ).reduce((a, b) => a + b, 0),
                this.pairs[pair] ? this.pairs[pair].priceDecimals : undefined,
              )
              :
              fixedToWithoutRound(
                Number(price) * Number(amount),
                this.pairs[pair] ? this.pairs[pair].priceDecimals : undefined,
              )}
          </span>
        );
      },
    },
    {
      Header: t('Status'),
      accessor: 'status',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="CANCELED">{t('Cancelled')}</option>
          <option value="FILLED">{t('Filled')}</option>
          <option value="PENDING">{t('Pending')}</option>
          <option value="REJECTED">{t('Rejected')}</option>
        </select>
      ),
      Cell: props => <span className={getStatusColor(props.value)}>{mapStatus(props.value)}</span>,
    },
    {
      Header: t('Register Date'),
      accessor: 'createdAt',
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      minWidth: 120,
      filterable: true,
      Filter: ({onChange}) => (
        <ThemeProvider theme={createMuiTheme({
          typography: {
            fontFamily: 'IRANSans',
          },
        })}>
          <MuiPickersUtilsProvider
            utils={i18n.language === fa_IR ? JalaliUtils : MomentUtils}
            locale={i18n.language}
          >
            <DateTimePicker
              {...getDatePickerProps()}
              value={this.state.selectedStartDate}
              TextFieldComponent={props => <input style={{width: '100%'}} placeholder={t('start date')} {...props} />}
              onChange={date => {
                this.handleDateChange('selectedStartDate', date);
                onChange(date);
              }}
            />
            <div style={{height: 4}} />
            <DateTimePicker
              {...getDatePickerProps()}
              value={this.state.selectedEndDate}
              TextFieldComponent={props => <input style={{width: '100%'}} placeholder={t('end date')} {...props} />}
              onChange={date => {
                this.handleDateChange('selectedEndDate', date);
                onChange(date);
              }}
            />
          </MuiPickersUtilsProvider>
        </ThemeProvider>
      ),
    },
    {
      Header: t('Actions'),
      Cell: props => props.original.status === 'PENDING' ?
        <div
          className="text-danger cursor-pointer"
          onClick={() => {this.onPressCancel(props.original.id);}}
        >
          <i className="simple-icon-close iconsminds-close" />
          <span className="mx-1">{t('Cancel')}</span>
        </div>
        :
        null,
      show: Rbac.hasPermission('update_user-open-orders'),
    },
  ];

  getStopColumns = t => [
    {
      Header: t('Pair'),
      accessor: 'pair',
      filterable: true,
      minWidth: 120,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          {values(this.pairs).map(pair => (
            <option key={pair.name} value={pair.name}>{pair.localeName}</option>
          ))}
        </select>
      ),
      Cell: props => <span>{this.pairs[props.value] ? this.pairs[props.value].localeName : props.value}</span>,
    },
    {
      Header: t('Type'),
      accessor: 'type',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="LIMIT">{t('Limit')}</option>
          <option value="MARKET">{t('Market')}</option>
        </select>
      ),
      Cell: props => <span>{t(capitalize(props.value))}</span>
    },
    {
      Header: t('Side'),
      accessor: 'side',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="BUY">{t('Buy')}</option>
          <option value="SELL">{t('Sell')}</option>
        </select>
      ),
      Cell: props => <span className={getSideColor(props.value)}>{t(capitalize(props.value))}</span>,
    },
    {
      Header: t('Stop Price'),
      accessor: 'conditions',
      Cell: props => {
        if (props.value && props.value.length) {
          const orderConditions = keyBy(props.value, 'condition');
          const stopOrderCondition = orderConditions[`STOP_${props.original.type}`];
          const stopPrice = stopOrderCondition ? stopOrderCondition.price : null;
          return (
            <span>
              {props.original.type === 'MARKET' ?
                '---'
                :
                stopPrice ? numberWithCommas(Number(stopPrice)) : '---'
              }
            </span>
          );
        }
        return null;
      },
    },
    {
      Header: t('Price'),
      accessor: 'price',
      Cell: props => {
        const {side, type} = props.original;
        return (
          <span className={type !== 'MARKET' ? getSideColor(side) : null}>
            {type === 'MARKET' ?
              '---'
              :
              numberWithCommas(Number(props.value))
            }
          </span>
        );
      },
    },
    {
      Header: t('Amount'),
      accessor: 'amount',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>,
    },
    {
      Header: t('Status'),
      accessor: 'conditionStatus',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="PENDING">{t('Waiting')}</option>
          <option value="CONFIRMED">{t('Activated')}</option>
          <option value="CANCELED">{t('Cancelled')}</option>
        </select>
      ),
      Cell: props => {
        if (props.original.conditions && props.original.conditions.length) {
          const orderConditions = keyBy(props.original.conditions, 'condition');
          const stopOrderCondition = orderConditions[`STOP_${props.original.type}`];
          const status = stopOrderCondition ? stopOrderCondition.status : 'UNKNOWN';
          return (
            <span className={getStatusColor(status)}>
              {this.mapStopOrderStatus(status)}
            </span>
          );
        }
        return null;
      },
    },
    {
      Header: t('Register Date'),
      accessor: 'createdAt',
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      minWidth: 120,
    },
    {
      Header: t('Actions'),
      Cell: props => props.original.status === 'WAITING' ?
        <div
          className="text-danger cursor-pointer"
          onClick={() => {this.onPressCancel(props.original.id);}}
        >
          <i className="simple-icon-close iconsminds-close" />
          <span className="mx-1">{t('Cancel')}</span>
        </div>
        :
        null,
      show: Rbac.hasPermission('update_user-open-orders'),
    },
  ];

  render() {
    const {t} = this.props;
    const {selectedType} = this.state;
    return (
      <Row>
        <Colxx xxs="12" className="mb-4">
          <Card className="h-100">
            <CardBody>
              <CardTitle className="text-large text-primary d-flex flex-row justify-content-between align-items-center">
                {t('Orders')}
                {application.platformName === "zarindax" &&
                  <Rbac permission='create_user-orders'>
                    <Button onClick={this.onPressCreateOrder}>
                      <i className="simple-icon-plus add-reply-icon"/>
                      <span className="mx-1">{t('Place Order')}</span>
                    </Button>
                  </Rbac>
                }
              </CardTitle>
              <Row className='my-3' style={{maxWidth: 240}}>
                <Select
                  className="w-100 mx-3"
                  style={selectStyles}
                  onChange={this.onChangeType}
                  value={find(this.typeOptions, item => item.value === selectedType)}
                  options={this.typeOptions}
                />
              </Row>
              <ReactTable
                manual
                sortable={false}
                resizable={true}
                ref={el => {this.table = el;}}
                className="-highlight"
                data={this.state.orders}
                pages={this.state.pages}
                loading={this.state.loading}
                onFetchData={this.fetchData}
                columns={!selectedType ? this.getNormalColumns(t) : this.getStopColumns(t)}
                style={{width: '100%'}}
                defaultPageSize={10}
                {...reactTableText}
              />
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    );
  }
}

export default withTranslation()(Orders);
